"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var CacheStoreType_1 = __importDefault(require("../../enums/CacheStoreType"));
var utilities_1 = require("@ikea-aoa/utilities");
var exceptions_1 = require("@ikea-aoa/exceptions");
/**
 * This class is used for storing and loading products from cache.
 */
var ProductCache = /** @class */ (function () {
    /**
     * Constructor.
     *
     * @param [locale] Locale.
     * @param [cache] Cache store.
     */
    function ProductCache(locale, cache) {
        if (cache === void 0) { cache = null; }
        this.locale = null;
        this.cache = null;
        this.products = new Map();
        if (cache && !cache.getStore) {
            throw new exceptions_1.ServiceException('Failed to instanciate facade. Expected second parameter to be of type CacheStore.');
        }
        this.locale = locale;
        this.cache = cache || new utilities_1.GlobalCacheStore();
    }
    /**
     * Returns multiple cached products.
     *
     * @param items Array of items.
     * @param [fields] Fields.
     * @returns Products and missing items.
     */
    ProductCache.prototype.getProducts = function (items, fields) {
        if (fields === void 0) { fields = null; }
        var products = [];
        var missing = [];
        for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
            var item = items_1[_i];
            var product = this.getProduct(item, fields);
            if (product) {
                products.push(this.getProduct(item, fields));
            }
            else {
                missing.push(item);
            }
        }
        return { products: products, missing: missing };
    };
    /**
     * Adds multiple products.
     *
     * @param products Products.
     * @param [fields] Fields.
     */
    ProductCache.prototype.addProducts = function (products, fields) {
        if (fields === void 0) { fields = null; }
        for (var _i = 0, products_1 = products; _i < products_1.length; _i++) {
            var product = products_1[_i];
            this.addProduct(product, fields);
        }
    };
    /**
     * Removes products that has been added to this instance from cache.
     *
     * @param products Products.
     * @param [fields] Fields.
     */
    ProductCache.prototype.removeProducts = function (products, fields) {
        if (fields === void 0) { fields = null; }
        for (var _i = 0, products_2 = products; _i < products_2.length; _i++) {
            var product = products_2[_i];
            this.removeProduct(product, fields);
        }
    };
    /**
     * Clears all products that has been added to this instance from cache.
     *
     * @param [fields] Fields.
     */
    ProductCache.prototype.clear = function (fields) {
        if (fields === void 0) { fields = null; }
        for (var _i = 0, _a = Array.from(this.products.values()); _i < _a.length; _i++) {
            var product = _a[_i];
            this.removeProduct(product, fields);
        }
    };
    /**
     * Returns cached products.
     *
     * @param item Item.
     * @param [fields] Fields.
     * @returns Product.
     */
    ProductCache.prototype.getProduct = function (item, fields) {
        if (fields === void 0) { fields = null; }
        var cached = this.cache.getStore(CacheStoreType_1.default.product).get(this.getCacheKey(item, fields));
        return cached || null;
    };
    /**
     * Returns cache key for an item.
     *
     * @param item Product item.
     * @param [fields] Fields.
     * @returns Cache key.
     */
    ProductCache.prototype.getCacheKey = function (item, fields) {
        if (fields === void 0) { fields = null; }
        var fieldsString = fields ? fields.sort().join('_') : '';
        return this.locale + item.type.toLowerCase() + item.id + fieldsString;
    };
    /**
     * Removes a product that has been added to this instance from cache.
     *
     * @param product Product.
     * @param [fields] Fields.
     */
    ProductCache.prototype.removeProduct = function (product, fields) {
        if (fields === void 0) { fields = null; }
        if (product.content) {
            var localItem = {
                type: product.content.itemType,
                id: product.content.itemNoLocal
            };
            var globalItem = {
                type: product.content.itemType,
                id: product.content.itemNoGlobal
            };
            var localCacheKey = this.getCacheKey(localItem, fields);
            var globalCacheKey = this.getCacheKey(globalItem, fields);
            if (this.products.get(localCacheKey)) {
                this.products.delete(localCacheKey);
                this.cache.getStore(CacheStoreType_1.default.product).delete(localCacheKey);
            }
            if (this.products.get(globalCacheKey)) {
                this.products.delete(globalCacheKey);
                this.cache.getStore(CacheStoreType_1.default.product).delete(globalCacheKey);
            }
        }
        else {
            var _a = product.itemId.split('-'), type = _a[0], id = _a[1];
            var cacheKey = this.getCacheKey({ id: id, type: type }, fields);
            if (this.products.get(cacheKey)) {
                this.products.delete(cacheKey);
                this.cache.getStore(CacheStoreType_1.default.product).delete(cacheKey);
            }
        }
    };
    /**
     * Adds a product to cache.
     *
     * @param product Product.
     * @param [fields] Fields.
     */
    ProductCache.prototype.addProduct = function (product, fields) {
        if (fields === void 0) { fields = null; }
        if (product.content) {
            if (product.content.itemNoGlobal) {
                var globalCacheKey = this.getCacheKey({ id: product.content.itemNoGlobal, type: product.content.itemType }, fields);
                this.products.set(globalCacheKey, product);
                this.cache.getStore(CacheStoreType_1.default.product).set(globalCacheKey, product);
            }
            if (!product.content.itemNoGlobal ||
                product.content.itemNoGlobal !== product.content.itemNoLocal) {
                var localCacheKey = this.getCacheKey({ id: product.content.itemNoLocal, type: product.content.itemType }, fields);
                this.products.set(localCacheKey, product);
                this.cache.getStore(CacheStoreType_1.default.product).set(localCacheKey, product);
            }
        }
        else {
            var _a = product.itemId.split('-'), type = _a[0], id = _a[1];
            var cacheKey = this.getCacheKey({ id: id, type: type }, fields);
            this.products.set(cacheKey, product);
            this.cache.getStore(CacheStoreType_1.default.product).set(cacheKey, product);
        }
    };
    return ProductCache;
}());
exports.default = ProductCache;
//# sourceMappingURL=ProductCache.js.map